import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDeals } from '../api/hooks';
import { STAGE_LABEL, STAGE_ORDER, DealStage } from '../lib/types';
import { Stack, Row, Text, Badge, Panel, List, ListItem, Money, Tag, Button, Input, EmptyState, DealMenu } from '../ui';

export function Deals() {
  const { data: deals, loading, refresh } = useDeals();
  const [filter, setFilter] = useState<DealStage | 'all'>('all');
  const [q, setQ] = useState('');

  const visible = deals.filter((d: any) =>
    (filter === 'all' || d.stage === filter) &&
    (q === '' || d.name?.toLowerCase().includes(q.toLowerCase()) || d.sector?.toLowerCase().includes(q.toLowerCase()))
  );

  const totalSize = visible.reduce((a: number, d: any) => a + (d.ticketSize ?? 0), 0);

  return (
    <Stack gap={3}>
      {/* Header */}
      <Row justify="between" align="center" wrap>
        <Stack gap={0.5}>
          <Text variant="display">Deal book</Text>
          <Text muted variant="small">
            {visible.length} deal{visible.length === 1 ? '' : 's'}{totalSize > 0 ? <> · <Money amount={totalSize} currency="USD" /> aggregate</> : null}
          </Text>
        </Stack>
        <Row gap={1} className="shrink-0">
          <Link to="/deals/all"><Button variant="secondary" size="sm" className="h-11 sm:h-9">archive →</Button></Link>
          <Link to="/deals/new"><Button variant="primary" size="sm" className="h-11 sm:h-9">+ new deal</Button></Link>
        </Row>
      </Row>

      {/* Filters */}
      {!loading && deals.length > 0 && (
        <Stack gap={2}>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="search by name or sector…" className="w-full" />
          <div className="flex flex-wrap gap-1">
            {(['all', ...STAGE_ORDER] as const).map(s => (
              <button key={s} onClick={() => setFilter(s as any)}
                className={`h-9 sm:h-7 px-3 sm:px-2 hairline rounded-sm text-xs transition-colors ${
                  filter === s ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink'
                }`}>
                {s === 'all' ? 'all' : STAGE_LABEL[s]}
              </button>
            ))}
          </div>
        </Stack>
      )}

      {/* Deal list */}
      <Panel>
        {loading ? <Text variant="label">loading…</Text>
          : visible.length === 0
            ? <EmptyState
                title={deals.length === 0 ? 'no deals yet' : 'no deals match'}
                hint={deals.length === 0 ? 'create your first deal' : 'try a different filter or search'}
                action={deals.length === 0 ? <Link to="/deals/new"><Button variant="primary" size="sm">+ new deal</Button></Link> : undefined}
              />
            : <List>
                {visible.map((d: any) => (
                  <ListItem key={d.id}>
                    <Row justify="between" align="center" gap={2}>
                      <Link to={`/deals/${d.id}`} className="flex-1 min-w-0 block">
                        <Row justify="between" align="center" gap={2}>
                          <Stack gap={0.5} className="min-w-0 flex-1">
                            <Row gap={1} wrap>
                              <Text variant="small" className="font-medium truncate">{d.name}</Text>
                              {d.heat && <Badge tone={d.heat === 'hot' ? 'negative' : d.heat === 'warm' ? 'warning' : 'neutral'}>{d.heat}</Badge>}
                              <Tag>{STAGE_LABEL[d.stage as DealStage]}</Tag>
                            </Row>
                            <Row gap={2} wrap>
                              {d.sector && <Text variant="label">{d.sector}</Text>}
                              {d.geography && <Text variant="label">{d.geography}</Text>}
                            </Row>
                          </Stack>
                          {d.ticketSize && (
                            <Money amount={d.ticketSize} currency={d.currency || 'USD'} className="text-sm shrink-0" />
                          )}
                        </Row>
                      </Link>
                      <DealMenu dealId={d.id} state="active" onChange={refresh} />
                    </Row>
                  </ListItem>
                ))}
              </List>}
      </Panel>
    </Stack>
  );
}

